import React, { useEffect, useState } from 'react';
import GatewayV2Services from '../../services/externalServices/TicketServices/gatewayV2Services';
import TheatreServices from '../../services/externalServices/TicketServices/theatreServices';

const emptyForm = {
  serviceType: 'Tickets',
  serviceId: '',
  paymentTimeoutSec: 360,
  holdTimeoutSec: 0,
  webhookGraceSec: 120,
  cancelOnTimeout: true,
  confirmUrl: '',
  cancelUrl: '',
  // Optional overrides for outputs (if left blank, env defaults apply)
  outputs: {
    send: {
      ticketOnSuccess: '', // '', 'true', 'false'
      invoiceOnSuccess: ''
    },
    qr: {
      enabled: '',
      localDir: '',
      publicBaseUrl: ''
    },
    whatsapp: {
      enabled: ''
    }
  }
};

export default function ServicePaymentPolicy() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [theatres, setTheatres] = useState([]);
  const [theatresLoading, setTheatresLoading] = useState(false);
  const [outputsDefaults, setOutputsDefaults] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await GatewayV2Services.listServicePolicies();
      setItems(res?.data || res?.items || []);
    } catch (e) {
      setError('Failed to load policies');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const fetch = async () => {
      setTheatresLoading(true);
      try {
        const res = await TheatreServices.listTheatres();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setTheatres(list);
      } catch (_) { setTheatres([]); }
      finally { setTheatresLoading(false); }
    };
    fetch();
  }, []);

  useEffect(() => {
    const getDefaults = async () => {
      try {
        const res = await GatewayV2Services.getOutputsDefaults();
        const data = res?.data || res?.data?.data || res; // support various wrappers
        setOutputsDefaults(data?.data || data);
      } catch (_) {
        setOutputsDefaults(null);
      }
    };
    getDefaults();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (!form.serviceType) { setError('Service type is required'); return; }
      if (!form.serviceId) { setError('Service ID/Theatre is required'); return; }
      const toNum = (v) => { const n = Number(v); return isNaN(n)? 0 : n; };
      if (toNum(form.paymentTimeoutSec) < 0) { setError('Payment timeout must be >= 0'); return; }
      if (toNum(form.webhookGraceSec) < 0) { setError('Webhook grace must be >= 0'); return; }
      // Build outputs overrides, omitting blanks
      const outputs = {};
      const s = form.outputs?.send || {};
      const q = form.outputs?.qr || {};
      const w = form.outputs?.whatsapp || {};
      const coerceBool = (v) => v === '' ? undefined : (v === 'true');

      const send = {};
      if (s.ticketOnSuccess !== '') send.ticketOnSuccess = coerceBool(s.ticketOnSuccess);
      if (s.invoiceOnSuccess !== '') send.invoiceOnSuccess = coerceBool(s.invoiceOnSuccess);
      if (Object.keys(send).length) outputs.send = send;

      const qr = {};
      if (q.enabled !== '') qr.enabled = coerceBool(q.enabled);
      if (q.localDir) qr.localDir = q.localDir;
      if (q.publicBaseUrl) qr.publicBaseUrl = q.publicBaseUrl;
      if (Object.keys(qr).length) outputs.qr = qr;

      const whatsapp = {};
      if (w.enabled !== '') whatsapp.enabled = coerceBool(w.enabled);
      if (Object.keys(whatsapp).length) outputs.whatsapp = whatsapp;

      const payload = {
        serviceType: form.serviceType,
        serviceId: form.serviceId.trim(),
        paymentTimeoutSec: toNum(form.paymentTimeoutSec),
        holdTimeoutSec: toNum(form.holdTimeoutSec),
        webhookGraceSec: toNum(form.webhookGraceSec),
        cancelOnTimeout: !!form.cancelOnTimeout,
        confirmUrl: form.confirmUrl || undefined,
        cancelUrl: form.cancelUrl || undefined,
        ...(Object.keys(outputs).length ? { outputs } : {})
      };
      if (editingId) await GatewayV2Services.updateServicePolicy(editingId, payload);
      else await GatewayV2Services.createServicePolicy(payload);
      setForm(emptyForm); setEditingId(null);
      load();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const startEdit = (it) => {
    setEditingId(it._id);
    setForm({
      serviceType: it.serviceType || 'Tickets',
      serviceId: it.serviceId || '',
      paymentTimeoutSec: it.paymentTimeoutSec ?? 360,
      holdTimeoutSec: it.holdTimeoutSec ?? 0,
      webhookGraceSec: it.webhookGraceSec ?? 120,
      cancelOnTimeout: it.cancelOnTimeout !== false,
      confirmUrl: it.confirmUrl || '',
      cancelUrl: it.cancelUrl || '',
      outputs: {
        send: {
          ticketOnSuccess: it?.outputs?.send?.ticketOnSuccess === undefined ? '' : String(!!it.outputs.send.ticketOnSuccess),
          invoiceOnSuccess: it?.outputs?.send?.invoiceOnSuccess === undefined ? '' : String(!!it.outputs.send.invoiceOnSuccess)
        },
        qr: {
          enabled: it?.outputs?.qr?.enabled === undefined ? '' : String(!!it.outputs.qr.enabled),
          localDir: it?.outputs?.qr?.localDir || '',
          publicBaseUrl: it?.outputs?.qr?.publicBaseUrl || ''
        },
        whatsapp: {
          enabled: it?.outputs?.whatsapp?.enabled === undefined ? '' : String(!!it.outputs.whatsapp.enabled)
        }
      }
    });
  };

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setError(''); };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Payments v2 — Service Payment Policy</h2>
      {error && <div className="mb-3 text-red-600">{error}</div>}

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm">Service Type</label>
          <select className="border rounded px-3 py-2 w-full" value={form.serviceType} onChange={e=>setForm({...form, serviceType:e.target.value})}>
            {['Tickets','TourPackage','Taxi','Auto'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {form.serviceType === 'Tickets' ? (
          <div>
            <label className="block text-sm">Theatre</label>
            <select className="border rounded px-3 py-2 w-full" value={form.serviceId} onChange={e=>setForm({...form, serviceId:e.target.value})}>
              <option value="">Select theatre</option>
              {theatres.map(th => (
                <option key={th._id} value={th._id}>{th.name}</option>
              ))}
            </select>
            {theatresLoading && <div className="text-xs text-gray-500 mt-1">Loading theatres...</div>}
          </div>
        ) : (
          <div>
            <label className="block text-sm">Service ID</label>
            <input className="border rounded px-3 py-2 w-full" value={form.serviceId} onChange={e=>setForm({...form, serviceId:e.target.value})} placeholder="e.g., vendorId" />
          </div>
        )}
        <div>
          <label className="block text-sm">Payment Timeout (sec)</label>
          <input type="number" className="border rounded px-3 py-2 w-full" value={form.paymentTimeoutSec} onChange={e=>setForm({...form, paymentTimeoutSec:e.target.value})} />
        </div>
        <div>
          <label className="block text-sm">Hold Timeout (sec)</label>
          <input type="number" className="border rounded px-3 py-2 w-full" value={form.holdTimeoutSec} onChange={e=>setForm({...form, holdTimeoutSec:e.target.value})} />
        </div>
        <div>
          <label className="block text-sm">Webhook Grace (sec)</label>
          <input type="number" className="border rounded px-3 py-2 w-full" value={form.webhookGraceSec} onChange={e=>setForm({...form, webhookGraceSec:e.target.value})} />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={!!form.cancelOnTimeout} onChange={e=>setForm({...form, cancelOnTimeout:e.target.checked})} />
          <span>Cancel On Timeout</span>
        </div>
        <div>
          <label className="block text-sm">Default Confirm URL (optional)</label>
          <input className="border rounded px-3 py-2 w-full" value={form.confirmUrl} onChange={e=>setForm({...form, confirmUrl:e.target.value})} />
        </div>
        <div>
          <label className="block text-sm">Default Cancel URL (optional)</label>
          <input className="border rounded px-3 py-2 w-full" value={form.cancelUrl} onChange={e=>setForm({...form, cancelUrl:e.target.value})} />
        </div>
        <div className="md:col-span-2 flex gap-3">
          <button type="submit" className="px-4 py-2 bg-black text-white rounded">{editingId? 'Update' : 'Create'}</button>
          <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={resetForm}>Reset</button>
        </div>
      </form>

      <div className="mb-8">
        <h3 className="text-xl font-medium mb-2">Outputs (Overrides)</h3>
        <p className="text-sm text-gray-600 mb-4">Leave blank to use defaults. Booleans: choose Enable/Disable or Use default.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded p-3">
            <div className="font-semibold mb-2">Send</div>
            <label className="block text-sm">Ticket on success</label>
            <select className="border rounded px-2 py-1 w-full mb-2" value={form.outputs.send.ticketOnSuccess} onChange={e=>setForm({...form, outputs:{...form.outputs, send:{...form.outputs.send, ticketOnSuccess:e.target.value}}})}>
              <option value="">Use default ({String(outputsDefaults?.send?.ticketOnSuccess ?? false)})</option>
              <option value="true">Enable</option>
              <option value="false">Disable</option>
            </select>
            <label className="block text-sm">Invoice on success</label>
            <select className="border rounded px-2 py-1 w-full" value={form.outputs.send.invoiceOnSuccess} onChange={e=>setForm({...form, outputs:{...form.outputs, send:{...form.outputs.send, invoiceOnSuccess:e.target.value}}})}>
              <option value="">Use default ({String(outputsDefaults?.send?.invoiceOnSuccess ?? false)})</option>
              <option value="true">Enable</option>
              <option value="false">Disable</option>
            </select>
          </div>
          <div className="border rounded p-3">
            <div className="font-semibold mb-2">QR</div>
            <label className="block text-sm">Enabled</label>
            <select className="border rounded px-2 py-1 w-full mb-2" value={form.outputs.qr.enabled} onChange={e=>setForm({...form, outputs:{...form.outputs, qr:{...form.outputs.qr, enabled:e.target.value}}})}>
              <option value="">Use default ({String(outputsDefaults?.qr?.enabled ?? false)})</option>
              <option value="true">Enable</option>
              <option value="false">Disable</option>
            </select>
            <label className="block text-sm">Local Dir</label>
            <input className="border rounded px-2 py-1 w-full mb-2" placeholder={outputsDefaults?.qr?.localDir || ''} value={form.outputs.qr.localDir} onChange={e=>setForm({...form, outputs:{...form.outputs, qr:{...form.outputs.qr, localDir:e.target.value}}})} />
            <label className="block text-sm">Public Base URL</label>
            <input className="border rounded px-2 py-1 w-full" placeholder={outputsDefaults?.qr?.publicBaseUrl || ''} value={form.outputs.qr.publicBaseUrl} onChange={e=>setForm({...form, outputs:{...form.outputs, qr:{...form.outputs.qr, publicBaseUrl:e.target.value}}})} />
          </div>
          <div className="border rounded p-3">
            <div className="font-semibold mb-2">WhatsApp</div>
            <label className="block text-sm">Enabled</label>
            <select className="border rounded px-2 py-1 w-full mb-2" value={form.outputs.whatsapp.enabled} onChange={e=>setForm({...form, outputs:{...form.outputs, whatsapp:{...form.outputs.whatsapp, enabled:e.target.value}}})}>
              <option value="">Use default ({String(outputsDefaults?.whatsapp?.enabled ?? false)})</option>
              <option value="true">Enable</option>
              <option value="false">Disable</option>
            </select>
            <div className="text-xs text-gray-500">Provider default: {outputsDefaults?.whatsapp?.provider || '-'}</div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-medium mb-2">Existing Policies</h3>
        {loading ? <div>Loading...</div> : (
          <table className="min-w-full border">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-1 border">Service</th>
                <th className="px-2 py-1 border">Service Target</th>
                <th className="px-2 py-1 border">Timeout (s)</th>
                <th className="px-2 py-1 border">Grace (s)</th>
                <th className="px-2 py-1 border">Cancel?</th>
                <th className="px-2 py-1 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it._id}>
                  <td className="px-2 py-1 border">{it.serviceType}</td>
                  <td className="px-2 py-1 border">{it.serviceType === 'Tickets' ? (theatres.find(t=> String(t._id)===String(it.serviceId))?.name || it.serviceId) : it.serviceId}</td>
                  <td className="px-2 py-1 border">{it.paymentTimeoutSec}</td>
                  <td className="px-2 py-1 border">{it.webhookGraceSec}</td>
                  <td className="px-2 py-1 border">{it.cancelOnTimeout? 'Yes':'No'}</td>
                  <td className="px-2 py-1 border flex gap-2">
                    <button className="px-2 py-1 bg-gray-200 rounded" onClick={()=>startEdit(it)}>Edit</button>
                    <button className="px-2 py-1 bg-red-600 text-white rounded" onClick={async()=>{ if(window.confirm('Delete policy?')) { await GatewayV2Services.deleteServicePolicy(it._id); load(); } }}>Delete</button>
                  </td>
                </tr>
              ))}
              {items.length===0 && !loading && (
                <tr><td className="px-2 py-2 text-gray-500" colSpan={6}>No policies found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
